/**
 * W5160 — `useAuth` FE gate (F-AUTH-SEAM FE face).
 *
 * # What this does
 * Wraps {@link useFrontendSessionToken} and decodes the returned compact JWT
 * to expose the caller's identity (`currentUserId`) and a short-code permission
 * gate (`hasPermission`).
 *
 * # Claim layout (host-minted, per FrontendSessionTokenIssuer)
 * - `user_id` (or `sub` as fallback) — the caller's user ID.
 * - `permissions` — a space-delimited string of short codes,
 *   e.g. `"timeslip.read timeslip.write"`.
 *
 * # Fail-closed behaviour
 * While the token is loading, absent, or the bridge errors, `currentUserId`
 * is `null` and `hasPermission` always returns `false`.
 *
 * # Security note
 * The JWT signature is NOT re-validated here — the host validated it at mint.
 * The FE gate is advisory (UI gating); privileged execution is always
 * RBAC-gated host-side (§4.4 of the auth-seam spec).
 *
 * This is the canonical `useAuth` hook; `@ethisyscore/plugin-ui/token`
 * re-exports it so plugin authors take a single dependency on plugin-ui.
 */
import { useMemo } from "react";
import { useFrontendSessionToken } from "./useFrontendSessionToken";
import { decodeJwtPayload } from "./decodeJwtPayload";
import type { McpTransport } from "./transport";

export interface UseAuthResult
{
    /** The caller's user ID from the FE-session JWT, or null while loading / on error. */
    currentUserId: string | null;
    /**
     * Returns true if the caller has the given short-code permission.
     * Always returns false while the token is loading or absent (fail-closed).
     *
     * @param shortCode The exact permission short code, e.g. `"timeslip.read"`.
     */
    hasPermission(shortCode: string): boolean;
    /** True while the first fetch (or a silent refresh) is in-flight. */
    isLoading: boolean;
    /** Set when the bridge rejects; null otherwise. */
    error: Error | null;
}

/**
 * Returns the caller's identity and permission gate derived from the
 * FE-session token.
 *
 * @param transport The {@link McpTransport} to use. Typically the context
 * transport from the extension runtime provider.
 */
export function useAuth(transport: McpTransport): UseAuthResult
{
    const { token, isLoading, error } = useFrontendSessionToken(transport);

    const { currentUserId, permissionSet } = useMemo(() =>
    {
        const claims = decodeJwtPayload(token);

        const userId =
            (typeof claims["user_id"] === "string" && claims["user_id"] !== "" ? claims["user_id"] : null) ??
            (typeof claims["sub"] === "string" && claims["sub"] !== "" ? claims["sub"] : null);

        const permissionsRaw = typeof claims["permissions"] === "string" ? claims["permissions"] : "";
        const set = permissionsRaw.length > 0
            ? new Set(permissionsRaw.split(" ").filter((s) => s.length > 0))
            : new Set<string>();

        return { currentUserId: userId, permissionSet: set };
    }, [token]);

    return {
        currentUserId,
        hasPermission: (shortCode: string): boolean => permissionSet.has(shortCode),
        isLoading,
        error,
    };
}
